import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { Badge, PopoverBody, UncontrolledPopover } from 'reactstrap';
import { ProductAddonModel, ProductModel, ProductVisibility } from '../../../../shared/orders';
import { PlatformFeeHelp } from '../../../components';
import { Element, SearchProvider } from '../../../components/ElementSelector';
import { useTranslation } from '../../../translations';
import { capitalize } from '../../../utils';

/**
 * Search provider that performs all filtering on the client.
 */
export class LocalSearchProvider<T extends Element> implements SearchProvider<T> {
  public constructor(private readonly fetcher: () => Promise<T[]> | T[]) {}

  public async text(txt: string): Promise<T[]> {
    const lowerText = txt.toLocaleLowerCase();
    return (await this.fetcher()).filter(
      ({ id, name, displayName }) =>
        ((id && id.toString() === lowerText) || name?.toLocaleLowerCase().includes(lowerText)) ??
        displayName?.toLocaleLowerCase().includes(lowerText),
    );
  }

  public async ids(ids: number[]): Promise<T[]> {
    return (await this.fetcher()).filter(({ id }) => ids.includes(id));
  }
}

export const checkForPlatformFee = (product: ProductModel): boolean => {
  return product.surcharges.some(
    (surcharge) => surcharge.internal && surcharge.name.startsWith('platform-fee'),
  );
};

export const PlatformFeeBadge: FC<{ readonly product: ProductModel }> = ({ product }) => {
  const hasPlatformFee = checkForPlatformFee(product);
  const { ts } = useTranslation();
  return (
    <>
      <Badge color={hasPlatformFee ? 'success' : 'secondary'}>
        {ts(hasPlatformFee ? 'fees_passed_on_via_surcharge' : 'fees_absorbed_included')}
      </Badge>
      <PlatformFeeHelp />
    </>
  );
};

interface ProductInfoBadgesProps {
  readonly product: ProductModel;
}

// id, color, text
type BadgeInfo = [string, string, string];

export const ProductInfoBadges: FC<ProductInfoBadgesProps> = ({ product }) => {
  const { ts } = useTranslation();
  const idPrefix = `product${product.id}Status`;
  const badges: BadgeInfo[] = [];

  if (product.deletedAt) {
    badges.push(['Deleted', 'secondary', ts('common.deleted_archived')]);
  } else if (product.visibility === ProductVisibility.PUBLIC) {
    badges.push(['Public', 'success', ts('public_visible')]);
  } else if (product.visibility === ProductVisibility.UNLISTED) {
    badges.push(['Unlisted', 'info', ts('unlisted')]);
  } else {
    badges.push(['Hidden', 'secondary', ts('hidden')]);
  }

  if (product.stockMaximumQuantity !== null) {
    if (product.stockStatus !== 'soldOut') {
      badges.push([
        'InStock',
        'success',
        ts('product.settings.instock', product.stockAvailable!, product.stockMaximumQuantity!),
      ]);
    } else if (product.stockAvailable && product.stockAvailable < 0) {
      badges.push([
        'OutOfStockOversold',
        'warning',
        `Out of Stock - Exceeded by ${-product.stockAvailable}`,
      ]);
    } else {
      badges.push(['OutOfStock', 'secondary', ts('product.settings.outofstock')]);
    }
  }

  for (const addon of product.addons) {
    if (!addon.isFree) {
      continue;
    }

    if (addon.stockStatus === 'soldOut') {
      badges.push(['AddonOutOfStock', 'secondary', ts('product.settings.addonoutofstock')]);
    }

    if (addon.stockMaximumQuantity !== null) {
      badges.push([
        'InStock',
        'success',
        ts('product.settings.addonstock', addon.stockAvailable!, addon.stockMaximumQuantity!),
      ]);
    }
  }

  if (product.maxGrants) {
    badges.push(['limitGrants', 'secondary', `Limit per user: ${product.maxGrants}`]);
  }

  if (product.roleRestriction !== null) {
    badges.push(['roleRestriction', 'secondary', ts('product.settings.rolerestriction')]);
  }

  if (product.roleBlacklist !== null) {
    badges.push(['roleBlacklist', 'secondary', ts('product.settings.blacklist')]);
  }

  if (product.digitalGrantRestricted) {
    badges.push(['digitalGrantRestricted', 'secondary', ts('product.settings.grantrestriction')]);
  }

  if (product.availableFrom && product.availableFrom.getTime() > Date.now()) {
    badges.push(['notYetAvailable', 'danger', ts('product.settings.notyetavailable')]);
  }

  if (product.availableTo && product.availableTo.getTime() < Date.now()) {
    badges.push(['Expired', 'danger', ts('common.expired')]);
  }

  if (product.minAge || product.maxAge) {
    badges.push([
      'AgeRestricted',
      'warning',
      `
      Age Restricted: ${product.minAge && !product.maxAge ? '>=' : ''} ${product.minAge ?? ''}
      ${product.minAge && product.maxAge ? ' – ' : ''}
      ${product.maxAge && !product.minAge ? '<=' : ''} ${product.maxAge ?? ''}
    `,
    ]);
  }

  return (
    <div className="badge-list compact">
      {badges.map(([id, color, text]) => (
        <Badge color={color} id={`${idPrefix}${capitalize(id)}`} key={id}>
          {text}
        </Badge>
      ))}
      {!!product.unusedStock && product.unusedStock > 0 && (
        <Link
          className="badge"
          to={`/housekeeping/products/${product.id}/orders?page=0&filters=stock,no_grant`}
        >
          <Badge color="info" id={`${idPrefix}UnusedStock`}>
            {ts('utils.unused_stock')}: {product.unusedStock}
          </Badge>
          <UncontrolledPopover placement="bottom" target={`${idPrefix}UnusedStock`} trigger="hover">
            <PopoverBody>{ts('utils.unused_stock_help')}</PopoverBody>
          </UncontrolledPopover>
        </Link>
      )}
    </div>
  );
};

export const AddonInfoBadges: FC<{ readonly addon: ProductAddonModel }> = ({ addon }) => {
  const { ts } = useTranslation();
  const idPrefix = `addon${addon.id}Status`;
  const badges: BadgeInfo[] = [];

  const remainingStock = addon.stockAvailable;

  if (addon.isFree) {
    badges.push(['Free', 'success', ts('included_free')]);
  } else {
    badges.push(['NotFree', 'secondary', ts('not_included_extra_purchase')]);
  }

  if (remainingStock === undefined) {
    // noop, this should be unreachable
  } else if (remainingStock === null) {
    badges.push(['Unlimited', 'success', 'In Stock / Unlimited']);
  } else if (remainingStock > 0) {
    badges.push([
      'InStock',
      'success',
      `In Stock: ${remainingStock}/${addon.stockMaximumQuantity ?? 'TODO'} Available`,
    ]);
  } else if (remainingStock === 0) {
    badges.push(['OutOfStock', 'secondary', 'Out of Stock']);
  } else {
    badges.push(['OutOfStockOversold', 'warning', `Out of Stock - Exceeded by ${-remainingStock}`]);
  }

  return badges.map(([id, color, text]) => (
    <>
      <Badge color={color} id={`${idPrefix}${capitalize(id)}`} key={id}>
        {text}
      </Badge>{' '}
    </>
  ));
};
